import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/adminApi';
import { athensYmd } from '../../utils/athensDate';
import { dfmt } from './supportCommon';

/**
 * Κάρτα καταστήματος → «Χρεώσεις» (βήμα 7, 27/10/2026): η κατάσταση με μια ματιά — εγκατάσταση, κύκλος και τιμή,
 * επόμενη πληρωμή (ή πόσο έχει λήξει), τελευταία πληρωμή, πακέτο παραστατικών του παρόχου, εξοπλισμός. Αλλαγές και
 * πληρωμές γίνονται στη σελίδα «Χρεώσεις» του πάνελ («Άνοιγμα στις Χρεώσεις» ανοίγει το κατάστημα εκεί).
 */

const CYCLE: Record<string, string> = {
  monthly: 'Μηνιαίος', bimonthly: 'Διμηνιαίος', quarterly: 'Τριμηνιαίος', semiannual: 'Εξαμηνιαίος', annual: 'Ετήσιος',
};
const eur = (v: any) => `${(parseFloat(v) || 0).toFixed(2).replace('.', ',')} €`;
const num = (n: number) => n.toLocaleString('el-GR');
/** Ημέρες από σήμερα (Ελλάδα) ως την ημερομηνία· αρνητικό = πέρασε. */
export const daysFromToday = (ymd: string, today = athensYmd()): number =>
  Math.round((Date.parse(`${ymd.slice(0, 10)}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 864e5);

interface Detail {
  billing: any | null;
  payments: Array<{ id: number; paid_at: string; amount: string; period_covered: string | null }>;
  hardware: Array<{ price: string; paid_amount: string }>;
  usage?: { docs_used: number; docs_raw: number };
}

const Fact: React.FC<{ label: string; children: React.ReactNode; tone?: 'danger' | 'warn' }> = ({ label, children, tone }) => (
  <div className="bg-surface-2 dark:bg-surface-2/40 border border-line-strong rounded-xl px-3 py-2.5">
    <p className="text-xs text-t3">{label}</p>
    <p className={`text-sm font-medium ${tone === 'danger' ? 'text-red-700 dark:text-red-400' : tone === 'warn' ? 'text-amber-700 dark:text-amber-400' : 'text-t1'}`}>
      {children}
    </p>
  </div>
);

const SupportBilling: React.FC<{ storeId: number; onOpenBilling?: (storeId: number) => void }> = ({ storeId, onOpenBilling }) => {
  const [d, setD] = useState<Detail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setD(null); setFailed(false);
    adminAPI.getStoreBilling(storeId)
      .then((r: any) => { if (alive) setD(r); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [storeId]);

  const open = onOpenBilling ? (
    <button type="button" onClick={() => onOpenBilling(storeId)} data-testid="support-billing-open"
      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-line text-t2 hover:text-t1 hover:bg-surface-3">
      Άνοιγμα στις Χρεώσεις <ArrowRight className="h-4 w-4" />
    </button>
  ) : null;

  if (failed) return <p className="text-sm text-t3">Οι χρεώσεις δεν φορτώθηκαν.</p>;
  if (!d) return <p className="flex items-center gap-2 text-sm text-t3"><Loader2 className="h-4 w-4 animate-spin" />Φόρτωση…</p>;

  const b = d.billing;
  if (!b || !b.installed_at) {
    return (
      <div className="space-y-3" data-testid="support-billing">
        <p className="text-sm text-t2">Δεν έχει γραφτεί ημερομηνία εγκατάστασης — χωρίς αυτήν δεν υπάρχει ημερολόγιο πληρωμών.</p>
        {open}
      </div>
    );
  }

  const due = b.next_due_date ? daysFromToday(String(b.next_due_date)) : null;
  const priced = b.agreed_price != null;
  const dueText = b.next_due_date == null ? 'Η σύμβαση ολοκληρώθηκε'
    : !priced ? `${dfmt(b.next_due_date)} · χωρίς τιμή`
      : due! < 0 ? `Έληξε πριν από ${-due!} ${-due! === 1 ? 'ημέρα' : 'ημέρες'} (${dfmt(b.next_due_date)})`
        : due === 0 ? 'Σήμερα' : `${dfmt(b.next_due_date)} · σε ${due} ${due === 1 ? 'ημέρα' : 'ημέρες'}`;
  const dueTone = priced && due !== null ? (due < -15 ? 'danger' : due < 0 ? 'warn' : undefined) : undefined;
  const last = d.payments[0] || null;
  const quota = b.provider_doc_quota ? Number(b.provider_doc_quota) : null;
  const used = d.usage?.docs_used ?? 0;
  const pct = quota ? Math.round((used / quota) * 100) : null;
  const hwTotal = d.hardware.reduce((t, h) => t + (parseFloat(h.price) || 0), 0);
  const hwOwed = d.hardware.reduce((t, h) => t + Math.max(0, (parseFloat(h.price) || 0) - (parseFloat(h.paid_amount) || 0)), 0);

  return (
    <div className="space-y-3" data-testid="support-billing">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Fact label="Εγκατάσταση">{dfmt(b.installed_at)}</Fact>
        <Fact label="Κύκλος και τιμή" tone={priced ? undefined : 'warn'}>
          {CYCLE[b.billing_cycle] || b.billing_cycle} · {priced ? eur(b.agreed_price) : 'χωρίς συμφωνημένη τιμή'}
        </Fact>
        <Fact label="Επόμενη πληρωμή" tone={dueTone}>{dueText}</Fact>
        <Fact label="Τελευταία πληρωμή">{last ? `${dfmt(last.paid_at)} · ${eur(last.amount)}${last.period_covered ? ` · ${last.period_covered}` : ''}` : 'Καμία καταγεγραμμένη'}</Fact>
        <Fact label="Πακέτο παραστατικών" tone={pct !== null && pct >= 95 ? 'danger' : pct !== null && pct >= 80 ? 'warn' : undefined}>
          {quota ? `${num(used)} / ${num(quota)} (${pct}%)` : `Χωρίς πακέτο · ${num(used)} παραστατικά`}
        </Fact>
        <Fact label="Εξοπλισμός" tone={hwOwed > 0 ? 'warn' : undefined}>
          {d.hardware.length ? `${d.hardware.length} · ${eur(hwTotal)}${hwOwed > 0 ? ` · χρωστά ${eur(hwOwed)}` : ' · εξοφλημένος'}` : 'Κανένας'}
        </Fact>
      </div>
      {b.tracking_from && (
        <p className="text-xs text-t3">Οι πληρωμές καταγράφονται από {dfmt(b.tracking_from)} — οι περίοδοι πριν από αυτήν δεν μετρούν ως χρέος.</p>
      )}
      {open}
    </div>
  );
};

export default SupportBilling;
